import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, CheckCircle, XCircle, AlertTriangle, Shield, FileCheck, Upload, Loader2, Search, Pencil, Trash2, FileDown, Building2 } from 'lucide-react';
import { SortSelect } from '../../components/SortSelect';
import { useServerList } from '../../hooks/useServerList';
import Pagination from '../../components/Pagination';
import { exportTableToPDF } from '../../utils/html2pdfExport';
import { ETIKET_PDF, SONUC_PDF, EVET_HAYIR_PDF } from '../../utils/pkPdfStyles';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SONUC_ETIKETLERI = [
  { value: 'yesil', label: 'Yeşil (Kusursuz)', bg: 'bg-green-600' },
  { value: 'mavi', label: 'Mavi (Hafif Kusurlu)', bg: 'bg-blue-600' },
  { value: 'sari', label: 'Sarı (Kusurlu)', bg: 'bg-yellow-600 text-black' },
  { value: 'kirmizi', label: 'Kırmızı (Güvensiz)', bg: 'bg-red-600' }
];

const GENEL_SONUCLAR = [
  { value: 'uygun', label: 'Uygun' },
  { value: 'sartli_uygun', label: 'Şartlı Uygun' },
  { value: 'uygun_degil', label: 'Uygun Değil' }
];

const ADIM_IKON = {
  basarili: <CheckCircle className="w-5 h-5 text-green-400" />,
  basarisiz: <XCircle className="w-5 h-5 text-red-400" />,
  bekliyor: <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
};

export default function PKKontroller() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const canEdit = (isAdmin || !!user?.has_pk_access) && !user?.is_viewer;

  const [ekipmanlar, setEkipmanlar] = useState([]);
  const [binalar, setBinalar] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [binaFiltre, setBinaFiltre] = useState('_all');
  const [exportingPdf, setExportingPdf] = useState(false);

  const SORT_MAP = {
    newest: 'created_at:desc', oldest: 'created_at:asc',
    tarih_yeni: 'kontrol_tarihi:desc', tarih_eski: 'kontrol_tarihi:asc',
    sonuc_az: 'genel_sonuc:asc',
  };
  const [sortKey, setSortKey] = useState('newest');

  const raporList = useServerList('/pk/kontrol-raporlari', {
    pageSize: 100,
    initialSort: 'created_at:desc',
  });

  useEffect(() => {
    raporList.setSort(SORT_MAP[sortKey] || 'created_at:desc');
  }, [sortKey]);// eslint-disable-line

  const [form, setForm] = useState({
    bina_id: '', ekipman_tipi_filtre: '', ekipman_id: '', kontrol_tarihi: '',
    muayene_kurulusu_firma_id: '',
    ekipnet_kayit_tipi: 'kalici', ekipnet_askiya_alma_tarihi: '',
    sonuc_etiket: '', genel_sonuc: 'uygun',
    tespit_edilen_kusurlar: [], aciklama: ''
  });

  const [yeniKusur, setYeniKusur] = useState({ kusur_kodu: '', aciklama: '', oncelik: 'normal' });
  const [yeniKusurKodsuz, setYeniKusurKodsuz] = useState({ aciklama: '', oncelik: 'normal' });

  useEffect(() => { fetchMeta(); }, []);

  const fetchMeta = async () => {
    try {
      const [eRes, bRes, fRes] = await Promise.all([
        axios.get(`${API}/pk/ekipmanlar`, { withCredentials: true }),
        axios.get(`${API}/pk/binalar`, { withCredentials: true }),
        axios.get(`${API}/pk/firmalar`, { withCredentials: true })
      ]);
      setEkipmanlar(eRes.data);
      setBinalar(bRes.data);
      setFirmalar(fRes.data);
    } catch (err) { console.error(err); }
    finally { setLoadingMeta(false); }
  };

  const addKusur = () => {
    if (!yeniKusur.aciklama) return;
    setForm({ ...form, tespit_edilen_kusurlar: [...form.tespit_edilen_kusurlar, { ...yeniKusur }] });
    setYeniKusur({ kusur_kodu: '', aciklama: '', oncelik: 'normal' });
  };

  const addKusurKodsuz = () => {
    if (!yeniKusurKodsuz.aciklama) return;
    setForm({ ...form, tespit_edilen_kusurlar: [...form.tespit_edilen_kusurlar, { kusur_kodu: '', ...yeniKusurKodsuz }] });
    setYeniKusurKodsuz({ aciklama: '', oncelik: 'normal' });
  };

  const removeKusur = (i) => {
    setForm({ ...form, tespit_edilen_kusurlar: form.tespit_edilen_kusurlar.filter((_, idx) => idx !== i) });
  };

  const handleSubmit = async () => {
    if (!form.ekipman_id || !form.kontrol_tarihi) return;
    setSubmitting(true);
    setSonuc(null);
    try {
      if (editMode && editingId) {
        // Düzenleme: PUT
        await axios.put(`${API}/pk/kontrol-raporlari/${editingId}`, form, { withCredentials: true });
        toast.success('Rapor güncellendi — ekipman durumu eşitlendi');
        setDialogOpen(false);
        resetForm();
        raporList.refetch();
        fetchMeta();
      } else {
        // Yeni yükleme
        const res = await axios.post(`${API}/pk/kontrol-raporlari/yukle`, form, { withCredentials: true });
        setSonuc(res.data);
        if (res.data.success) {
          raporList.refetch();
          fetchMeta();
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      if (editMode) toast.error('Güncelleme başarısız: ' + msg);
      else setSonuc({ success: false, hata: msg, dogrulama_adimlari: [] });
    } finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setForm({
      bina_id: '', ekipman_tipi_filtre: '', ekipman_id: '', kontrol_tarihi: '',
      muayene_kurulusu_firma_id: '',
      ekipnet_kayit_tipi: 'kalici', ekipnet_askiya_alma_tarihi: '',
      sonuc_etiket: '', genel_sonuc: 'uygun',
      tespit_edilen_kusurlar: [], aciklama: ''
    });
    setEditMode(false);
    setEditingId(null);
    setSonuc(null);
  };

  const handleEdit = (rapor) => {
    const ekipman = ekipmanlar.find(e => e.id === rapor.ekipman_id);
    setForm({
      bina_id: rapor.bina_id || ekipman?.bina_id || '',
      ekipman_tipi_filtre: ekipman?.ekipman_tipi || '',
      ekipman_id: rapor.ekipman_id || '',
      kontrol_tarihi: rapor.kontrol_tarihi || '',
      muayene_kurulusu_firma_id: rapor.muayene_kurulusu_firma_id || '',
      ekipnet_kayit_tipi: rapor.ekipnet_kayit_tipi || 'kalici',
      ekipnet_askiya_alma_tarihi: rapor.ekipnet_askiya_alma_tarihi || '',
      sonuc_etiket: rapor.sonuc_etiket || '',
      genel_sonuc: rapor.genel_sonuc || 'uygun',
      tespit_edilen_kusurlar: rapor.tespit_edilen_kusurlar || [],
      aciklama: rapor.aciklama || ''
    });
    setEditMode(true);
    setEditingId(rapor.id);
    setSonuc(null);
    setDialogOpen(true);
  };

  const handleDelete = async (rapor) => {
    if (!isAdmin) return;
    const ekipmanAdi = getEkipmanAdi(rapor.ekipman_id);
    if (!window.confirm(`"${ekipmanAdi}" raporunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    setDeletingId(rapor.id);
    try {
      await axios.delete(`${API}/pk/kontrol-raporlari/${rapor.id}`, { withCredentials: true });
      toast.success('Rapor silindi — ekipman durumu kalan raporlardan güncellendi');
      raporList.refetch();
      fetchMeta();
    } catch (err) {
      toast.error('Silme başarısız: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const getEkipmanAdi = (id) => { const e = ekipmanlar.find(x => x.id === id); return e ? `${e.alt_tip || e.ekipman_tipi} (${e.seri_no})` : id; };
  const getBinaAdi = (id) => binalar.find(b => b.id === id)?.bina_adi || id;
  const getFirmaAdi = (id) => firmalar.find(f => f.id === id)?.unvan || id || '—';
  // Rapor için bina adı: raporda bina_id yoksa ekipman üzerinden bul
  const getRaporBina = (r) => {
    const bid = r.bina_id || ekipmanlar.find(e => e.id === r.ekipman_id)?.bina_id;
    if (!bid) return '—';
    return binalar.find(b => b.id === bid)?.bina_adi || '—';
  };

  const handleBinaFiltre = (v) => {
    setBinaFiltre(v);
    raporList.setFilter('bina_id', v === '_all' ? '' : v);
  };

  // Tablo tabanlı PDF export (tüm filtrelenmiş kayıtlar)
  const exportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const rows = await raporList.fetchAll();
      if (!rows.length) { toast.error('Aktarılacak kayıt bulunamadı'); return; }
      const headers = ['Ekipman', 'Bina', 'Muayene Kuruluşu', 'Kontrol Tarihi', 'Etiket', 'Genel Sonuç', 'İSG-KATİP', 'EKİPNET', 'Açıklama'];
      const data = rows.map(r => [
        getEkipmanAdi(r.ekipman_id),
        getRaporBina(r),
        getFirmaAdi(r.muayene_kurulusu_firma_id),
        r.kontrol_tarihi || '-',
        SONUC_ETIKETLERI.find(e => e.value === r.sonuc_etiket)?.label.split(' ')[0] || '-',
        r.genel_sonuc === 'uygun' ? 'Uygun' : r.genel_sonuc === 'sartli_uygun' ? 'Şartlı Uygun' : 'Uygun Değil',
        r.isgkatip_sozlesme_dogrulandi ? 'Evet' : 'Hayır',
        r.ekipnet_dogrulandi ? 'Evet' : 'Hayır',
        r.aciklama || '-',
      ]);
      const binaAdi = binaFiltre !== '_all' ? getBinaAdi(binaFiltre) : 'Tüm Binalar';
      // Etiket (4), Genel Sonuç (5), İSG-KATİP (6), EKİPNET (7) hücreleri ekrandaki rozet renkleriyle
      const cellStyler = (r, c, raw) => {
        if (c === 4) return ETIKET_PDF[raw] || null;
        if (c === 5) return SONUC_PDF[raw] || null;
        if (c === 6 || c === 7) return EVET_HAYIR_PDF[raw] || null;
        return null;
      };
      await exportTableToPDF(
        headers, data,
        'Periyodik Kontrol Raporları',
        `Periyodik-Kontrol-Raporlari-${new Date().toISOString().slice(0, 10)}.pdf`,
        { subtitle: `${binaAdi} · ${rows.length} kayıt`, orientation: 'landscape', pageSize: 'a4', cellStyler }
      );
      toast.success('PDF indirildi');
    } catch (e) {
      toast.error('PDF oluşturulamadı: ' + (e?.message || e));
    } finally {
      setExportingPdf(false);
    }
  };

  const selectedEkipman = ekipmanlar.find(e => e.id === form.ekipman_id);
  const isAsansor = selectedEkipman?.ekipman_tipi === 'asansor';

  if (loadingMeta) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  const sortedRaporlar = raporList.items;
  const sayfaOzet = {
    uygun: sortedRaporlar.filter(r => r.genel_sonuc === 'uygun').length,
    sartli: sortedRaporlar.filter(r => r.genel_sonuc === 'sartli_uygun').length,
    degil: sortedRaporlar.filter(r => r.genel_sonuc === 'uygun_degil').length,
  };

  return (
    <div className="space-y-6" data-testid="pk-kontroller">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><Shield className="w-5 h-5 text-blue-400" /> Periyodik Kontrol Raporları</h2>
          <p className="text-sm text-gray-400">{raporList.total} rapor yüklendi {raporList.loading && '· yükleniyor...'}</p>
          <div className="flex items-center gap-2 mt-1.5 text-[11px]" data-testid="kontrol-ozet">
            <span className="px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 border border-green-700/40">Uygun: {sayfaOzet.uygun}</span>
            <span className="px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-300 border border-yellow-700/40">Şartlı: {sayfaOzet.sartli}</span>
            <span className="px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 border border-red-700/40">Uygun Değil: {sayfaOzet.degil}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={raporList.search}
              onChange={(e) => raporList.setSearch(e.target.value)}
              placeholder="Rapor no, muayeneci, firma..."
              className="bg-gray-800 border-gray-700 text-white pl-8 h-9 w-[220px]"
              data-testid="kontrol-search-input"
            />
          </div>
          <Select value={binaFiltre} onValueChange={handleBinaFiltre}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 w-[190px]" data-testid="kontrol-bina-filtre">
              <Building2 className="w-4 h-4 mr-1 text-gray-400" />
              <SelectValue placeholder="Bina filtrele" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[280px]">
              <SelectItem value="_all">Tüm Binalar</SelectItem>
              {binalar.map(b => <SelectItem key={b.id} value={b.id}>{b.bina_adi}</SelectItem>)}
            </SelectContent>
          </Select>
          <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
            { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
            { value: 'tarih_yeni', label: 'Kontrol Tarihi (Yeni→Eski)' }, { value: 'tarih_eski', label: 'Kontrol Tarihi (Eski→Yeni)' },
            { value: 'sonuc_az', label: 'Sonuç (A-Z)' }
          ]} />
          <Button onClick={exportPdf} disabled={exportingPdf} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700 h-9" data-testid="kontrol-pdf-btn">
            {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} PDF İndir
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-rapor-btn">
            <Upload className="w-4 h-4 mr-2" /> Rapor Yükle
          </Button>
        </div>
      </div>

      {/* Rapor Listesi */}
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-xs text-gray-400 bg-gray-800/50">
                  <th className="text-left py-3 px-4">Ekipman</th>
                  <th className="text-left py-3 px-4">Bina</th>
                  <th className="text-left py-3 px-4">Muayene Kuruluşu</th>
                  <th className="text-center py-3 px-4">Kontrol Tarihi</th>
                  <th className="text-center py-3 px-4">Etiket</th>
                  <th className="text-center py-3 px-4">Sonuç</th>
                  <th className="text-center py-3 px-4">İSG-KATİP</th>
                  <th className="text-center py-3 px-4">EKİPNET</th>
                  <th className="text-center py-3 px-4">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {raporList.total === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-gray-500">Henüz rapor yok</td></tr>
                ) : sortedRaporlar.map(r => {
                  const etiketCfg = SONUC_ETIKETLERI.find(e => e.value === r.sonuc_etiket);
                  const guncel = ekipmanlar.find(e => e.id === r.ekipman_id)?.son_rapor_id === r.id;
                  return (
                    <tr key={r.id} className="border-b border-gray-800/50 hover:bg-gray-800/30" data-testid={`rapor-row-${r.id}`}>
                      <td className="py-2 px-4 text-white text-xs">
                        {getEkipmanAdi(r.ekipman_id)}
                        {guncel && <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-300 border border-emerald-700/50 text-[10px]" title="Ekipman Yönetimi'ndeki durum bu rapordan türetiliyor" data-testid={`rapor-guncel-${r.id}`}>Güncel</span>}
                      </td>
                      <td className="py-2 px-4 text-gray-300 text-xs" data-testid={`rapor-bina-${r.id}`}>{getRaporBina(r)}</td>
                      <td className="py-2 px-4 text-gray-300 text-xs">{getFirmaAdi(r.muayene_kurulusu_firma_id)}</td>
                      <td className="py-2 px-4 text-center text-gray-400 text-xs">{r.kontrol_tarihi}</td>
                      <td className="py-2 px-4 text-center">
                        {etiketCfg && <Badge className={`${etiketCfg.bg} text-[10px]`}>{etiketCfg.label.split(' ')[0]}</Badge>}
                      </td>
                      <td className="py-2 px-4 text-center">
                        <Badge className={`text-[10px] ${r.genel_sonuc === 'uygun' ? 'bg-green-700' : r.genel_sonuc === 'sartli_uygun' ? 'bg-yellow-700 text-black' : 'bg-red-700'}`}>
                          {r.genel_sonuc === 'uygun' ? 'Uygun' : r.genel_sonuc === 'sartli_uygun' ? 'Şartlı' : 'Uygun Değil'}
                        </Badge>
                      </td>
                      <td className="py-2 px-4 text-center">{r.isgkatip_sozlesme_dogrulandi ? <CheckCircle className="w-4 h-4 text-green-400 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}</td>
                      <td className="py-2 px-4 text-center">{r.ekipnet_dogrulandi ? <CheckCircle className="w-4 h-4 text-green-400 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}</td>
                      <td className="py-2 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(r)}
                              className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                              title="Düzenle"
                              data-testid={`edit-rapor-${r.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(r)}
                              disabled={deletingId === r.id}
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              title="Sil (Yalnızca Admin)"
                              data-testid={`delete-rapor-${r.id}`}
                            >
                              {deletingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                          {!canEdit && !isAdmin && <span className="text-[10px] text-gray-600">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Server-side pagination */}
          <div className="border-t border-gray-700/40 px-3 py-2">
            <Pagination
              page={raporList.page}
              totalPages={raporList.totalPages}
              total={raporList.total}
              pageSize={raporList.pageSize}
              loading={raporList.loading}
              onPageChange={raporList.setPage}
              onPageSizeChange={raporList.setPageSize}
              testIdPrefix="kontrol-pagination"
            />
          </div>
        </CardContent>
      </Card>
      <Dialog open={dialogOpen} onOpenChange={v => { if (!v && dialogOpen) { setDialogOpen(false); resetForm(); return; } setDialogOpen(v); }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="rapor-form-title">
              {editMode ? 'Periyodik Kontrol Raporu Düzenle' : 'Periyodik Kontrol Raporu Yükle'}
            </DialogTitle>
          </DialogHeader>

          {!sonuc ? (
            <div className="space-y-4 py-2">
              {/* Ekipman Seçimi */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Bina</Label>
                  <Select value={form.bina_id || '_all'} onValueChange={v => setForm({ ...form, bina_id: v === '_all' ? '' : v, ekipman_id: '' })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Bina seçin" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="_all">Tümü</SelectItem>
                      {binalar.map(b => <SelectItem key={b.id} value={b.id}>{b.bina_adi}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ekipman Tipi</Label>
                  <Select value={form.ekipman_tipi_filtre || '_all'} onValueChange={v => setForm({ ...form, ekipman_tipi_filtre: v === '_all' ? '' : v, ekipman_id: '' })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="kontrol-ekipman-tipi"><SelectValue placeholder="Tip seçin" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[280px]">
                      <SelectItem value="_all">Tümü</SelectItem>
                      {Array.from(new Set(ekipmanlar.map(e => e.ekipman_tipi).filter(Boolean))).sort().map(tip => (
                        <SelectItem key={tip} value={tip}>{tip.charAt(0).toUpperCase() + tip.slice(1).replaceAll('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ekipman *</Label>
                  <Select value={form.ekipman_id} onValueChange={v => setForm({ ...form, ekipman_id: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Ekipman seçin" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[280px]">
                      {ekipmanlar
                        .filter(e => !form.bina_id || e.bina_id === form.bina_id)
                        .filter(e => !form.ekipman_tipi_filtre || e.ekipman_tipi === form.ekipman_tipi_filtre)
                        .map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.alt_tip || e.ekipman_tipi} — {e.seri_no}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kontrol Tarihi *</Label>
                  <Input type="date" value={form.kontrol_tarihi} onChange={e => setForm({ ...form, kontrol_tarihi: e.target.value })} className="bg-gray-800 border-gray-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label>Muayene Kuruluşu</Label>
                  <Select value={form.muayene_kurulusu_firma_id} onValueChange={v => setForm({ ...form, muayene_kurulusu_firma_id: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {firmalar.filter(f => f.firma_tipi === 'a_tipi_muayene' || f.firma_tipi === 'ekipman_muayene').map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.unvan}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* EKİPNET Doğrulama: ŞİMDİLİK DEVRE DIŞI (kullanıcı talebi) */}

              {/* Asansör Etiket */}
              {isAsansor && (
                <div className="space-y-2">
                  <Label>Asansör Etiket Rengi</Label>
                  <Select value={form.sonuc_etiket} onValueChange={v => setForm({ ...form, sonuc_etiket: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Etiket seçin" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {SONUC_ETIKETLERI.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Genel Sonuç</Label>
                <Select value={form.genel_sonuc} onValueChange={v => setForm({ ...form, genel_sonuc: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {GENEL_SONUCLAR.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Kusurlar */}
              <div className="space-y-2">
                <Label>Tespit Edilen Kusurlar ({form.tespit_edilen_kusurlar.length})</Label>
                {form.tespit_edilen_kusurlar.map((k, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded text-xs">
                    <Badge className={`text-[10px] ${k.oncelik === 'kritik' ? 'bg-red-700' : k.oncelik === 'yuksek' ? 'bg-orange-700' : 'bg-gray-700'}`}>{k.oncelik}</Badge>
                    <span className="text-gray-300 flex-1">{k.kusur_kodu && `[${k.kusur_kodu}] `}{k.aciklama}</span>
                    <button onClick={() => removeKusur(i)} className="text-red-400 hover:text-red-300 text-xs">x</button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input placeholder="Kod" value={yeniKusur.kusur_kodu} onChange={e => setYeniKusur({ ...yeniKusur, kusur_kodu: e.target.value })} className="bg-gray-800 border-gray-700 text-white h-8 text-xs w-20" />
                  <Input placeholder="Kusur açıklaması" value={yeniKusur.aciklama} onChange={e => setYeniKusur({ ...yeniKusur, aciklama: e.target.value })} className="bg-gray-800 border-gray-700 text-white h-8 text-xs flex-1" />
                  <Select value={yeniKusur.oncelik} onValueChange={v => setYeniKusur({ ...yeniKusur, oncelik: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-xs w-24"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="kritik">Kritik</SelectItem><SelectItem value="yuksek">Yüksek</SelectItem><SelectItem value="normal">Normal</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addKusur} size="sm" className="h-8 bg-gray-700 hover:bg-gray-600"><Plus className="w-3 h-3" /></Button>
                </div>
                {/* Kodsuz kusur satırı */}
                <div className="flex gap-2">
                  <Input placeholder="Kusur açıklaması (kodsuz)" value={yeniKusurKodsuz.aciklama} onChange={e => setYeniKusurKodsuz({ ...yeniKusurKodsuz, aciklama: e.target.value })} className="bg-gray-800 border-gray-700 text-white h-8 text-xs flex-1" data-testid="kusur-kodsuz-input" />
                  <Select value={yeniKusurKodsuz.oncelik} onValueChange={v => setYeniKusurKodsuz({ ...yeniKusurKodsuz, oncelik: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-xs w-24" data-testid="kusur-kodsuz-oncelik"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="kritik">Kritik</SelectItem><SelectItem value="yuksek">Yüksek</SelectItem><SelectItem value="normal">Normal</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addKusurKodsuz} size="sm" className="h-8 bg-gray-700 hover:bg-gray-600" data-testid="kusur-kodsuz-ekle-btn"><Plus className="w-3 h-3" /></Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea value={form.aciklama} onChange={e => setForm({ ...form, aciklama: e.target.value })} className="bg-gray-800 border-gray-700 text-white" rows={2} />
              </div>

              <DialogFooter>
                <Button onClick={() => { setDialogOpen(false); resetForm(); }} variant="ghost" className="text-gray-400">İptal</Button>
                <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-rapor-btn">
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editMode ? 'Güncelleniyor...' : 'Doğrulanıyor...'}</>
                  ) : (editMode ? 'Güncelle' : 'Doğrula & Yükle')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* Doğrulama Sonucu */
            <div className="space-y-4 py-2">
              <div className={`p-4 rounded-lg border ${sonuc.success ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {sonuc.success ? <CheckCircle className="w-5 h-5 text-green-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
                  <span className={`font-semibold ${sonuc.success ? 'text-green-400' : 'text-red-400'}`}>
                    {sonuc.success ? 'Rapor Başarıyla Yüklendi' : 'Doğrulama Başarısız'}
                  </span>
                </div>
                {sonuc.hata && <p className="text-sm text-red-300">{sonuc.hata}</p>}
              </div>

              {/* Doğrulama Adımları */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-400">Doğrulama Zinciri:</p>
                {sonuc.dogrulama_adimlari?.map((adim, i) => (
                  <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${adim.durum === 'basarili' ? 'bg-green-900/10 border-green-800/40' : 'bg-red-900/10 border-red-800/40'}`}>
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-800 text-xs font-bold text-gray-300">{adim.adim}</div>
                    {ADIM_IKON[adim.durum]}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{adim.baslik}</p>
                      <p className="text-xs text-gray-400">{adim.mesaj}</p>
                    </div>
                  </div>
                ))}
              </div>

              {sonuc.uyarilar?.length > 0 && (
                <div className="p-3 bg-yellow-900/20 border border-yellow-700/40 rounded-lg">
                  <p className="text-xs font-semibold text-yellow-400 mb-1">Uyarılar:</p>
                  {sonuc.uyarilar.map((u, i) => (
                    <p key={i} className="text-xs text-yellow-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {u}</p>
                  ))}
                </div>
              )}

              <DialogFooter>
                <Button onClick={() => { setSonuc(null); }} variant="ghost" className="text-gray-400">Yeni Rapor</Button>
                <Button onClick={() => { setDialogOpen(false); setSonuc(null); }} className="bg-gray-700 hover:bg-gray-600">Kapat</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
